import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import './AuthGate.css';
import { useAuth } from '../contexts/AuthContext';
import { apiPost, translateError } from '../services/apiService';
import { PlanOption } from './LandingPage';
import SEO from './SEO';

const NESTJS_URL = process.env.REACT_APP_NESTJS_URL || 'http://localhost:4000';

export default function RegisterScreen() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const selectedPlan: PlanOption | null = (location.state as any)?.selectedPlan ?? null;
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [step, setStep] = useState('');

  const validatePassword = (pw: string): string | null => {
    if (pw.length < 8) return 'A senha deve ter pelo menos 8 caracteres.';
    if (!/[A-Z]/.test(pw)) return 'A senha deve conter pelo menos uma letra maiúscula.';
    if (!/[a-z]/.test(pw)) return 'A senha deve conter pelo menos uma letra minúscula.';
    if (!/[0-9]/.test(pw)) return 'A senha deve conter pelo menos um número.';
    if (!/[^A-Za-z0-9]/.test(pw)) return 'A senha deve conter pelo menos um caractere especial (ex: !@#$%).';
    return null;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const pwError = validatePassword(password);
    if (pwError) {
      setError(pwError);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // Step 1 — Create Auth0 account
      setStep('Criando conta...');
      await apiPost<{ auth0UserId: string }>('/auth/register', { name, email, password });

      // Step 2 — Log in to get Auth0 token
      setStep('Autenticando...');
      const { accessToken } = await apiPost<{ accessToken: string }>('/auth/login', { email, password });
      localStorage.setItem('auth0_access_token', accessToken);

      // Step 3 — Free plan skips checkout; paid plans redirect to Stripe
      if (selectedPlan?.key === 'free') {
        login(accessToken);
        navigate('/app', { replace: true });
        return;
      }

      setStep('Preparando checkout...');
      const priceId = selectedPlan?.priceId || '';
      const planKey = selectedPlan?.key || '';

      const sessionRes = await fetch(`${NESTJS_URL}/checkout/create-session`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${accessToken}`,
        },
        body: JSON.stringify({ priceId, planKey, email }),
      });

      if (!sessionRes.ok) {
        const errText = await sessionRes.text();
        throw new Error(`Checkout error: ${errText.slice(0, 200)}`);
      }

      const { url } = await sessionRes.json() as { url: string };

      // Step 4 — Redirect to Stripe Checkout
      login(accessToken);
      window.location.href = url;
    } catch (err: any) {
      console.error('Registration error:', err);
      setError(translateError(err, 'Não foi possível criar a conta. Tente novamente.'));
    } finally {
      setLoading(false);
      setStep('');
    }
  };

  return (
    <div className="auth-gate">
      <SEO
        title="Criar Conta"
        description="Crie sua conta no MedPuzzle e comece a praticar diagnóstico médico com pacientes virtuais gerados por IA."
        path="/register"
      />
      <div className="auth-card">
        <div className="auth-icon-wrap">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
            <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
            <circle cx="9" cy="7" r="4" />
            <path d="M22 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75" />
          </svg>
        </div>
        <div className="auth-header">
          <h1 className="auth-card-title">Criar conta</h1>
          <p className="auth-card-desc">
            {selectedPlan ? `Plano ${selectedPlan.label}` : 'Cadastre-se para continuar'}
          </p>
        </div>
        <form className="auth-form" onSubmit={handleSubmit}>
          <div className="auth-field">
            <label htmlFor="reg-name">Nome completo</label>
            <div className="auth-input-wrapper">
              <svg className="auth-input-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                <circle cx="12" cy="7" r="4" />
              </svg>
              <input
                id="reg-name"
                type="text"
                autoComplete="name"
                required
                value={name}
                onChange={e => setName(e.target.value)}
                placeholder="Dr. João Silva"
                disabled={loading}
              />
            </div>
          </div>
          <div className="auth-field">
            <label htmlFor="reg-email">E-mail</label>
            <div className="auth-input-wrapper">
              <svg className="auth-input-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                <rect width="20" height="16" x="2" y="4" rx="2" />
                <path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7" />
              </svg>
              <input
                id="reg-email"
                type="email"
                autoComplete="email"
                required
                value={email}
                onChange={e => setEmail(e.target.value)}
                placeholder="voce@exemplo.com"
                disabled={loading}
              />
            </div>
          </div>
          <div className="auth-field">
            <label htmlFor="reg-password">Senha</label>
            <div className="auth-input-wrapper">
              <svg className="auth-input-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                <rect width="18" height="11" x="3" y="11" rx="2" ry="2" />
                <path d="M7 11V7a5 5 0 0 1 10 0v4" />
              </svg>
              <input
                id="reg-password"
                type="password"
                autoComplete="new-password"
                required
                minLength={8}
                value={password}
                onChange={e => setPassword(e.target.value)}
                placeholder="Mín. 8 chars, maiúscula, número e símbolo"
                disabled={loading}
              />
            </div>
          </div>
          {error && <p className="auth-error">{error}</p>}
          {loading && step && (
            <p style={{ fontSize: '0.8rem', color: '#64748b', margin: 0, textAlign: 'center' }}>{step}</p>
          )}
          <button type="submit" className="auth-submit" disabled={loading}>
            {loading ? 'Aguarde...' : selectedPlan?.key === 'free' ? 'Criar conta grátis' : 'Criar conta e pagar'}
          </button>
        </form>
        <div className="auth-divider">
          <span>ou</span>
        </div>
        <button
          type="button"
          className="auth-google-btn"
          onClick={() => { window.location.href = `${NESTJS_URL}/auth/google`; }}
          disabled={loading}
        >
          <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden="true">
            <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
            <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
            <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.84z" fill="#FBBC05"/>
            <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
          </svg>
          Registrar com Google
        </button>
        <button
          onClick={() => navigate('/login')}
          style={{ background: 'none', border: 'none', color: '#64748b', fontSize: '0.8rem', cursor: 'pointer', padding: 0 }}
        >
          Já tenho conta — entrar
        </button>
        <p className="auth-disclaimer">
          Apenas para fins educacionais e de treinamento. Não para uso clínico.
        </p>
      </div>
    </div>
  );
}
